import React, { useMemo } from 'react';
import { connect } from 'react-redux';
import { getTranslatedString } from 'helpers/i18n';
import Form from 'components/ui/Form';
import Spacer from 'components/ui/Spacer';
import Row from 'components/ui/Row';
import Col from 'components/ui/Col';
import Select from 'components/ui/SelectField';
import styled from 'styled-components';
import { sortAlphaNum } from 'helpers/utilities';
import { featureEnabled } from 'helpers/ToggleBot';
import { getTemplate, getTemplateValue, modes } from '../NoticeHelpers';
import TenantResidentVerbiageHelper from 'components/shared/TenantResidentVerbiageHelper';

const FullWidthSelect = styled(Select)`
  width: 100%;
`;

const StyledColMb8 = styled(Col)`
  margin-bottom: 8px;
`;

const StyledTitle = styled.span`
  font-size: 18px;
  font-family: 'Circular-Bold';
`;

const UrgentText = styled.span`
  font-size: 14px;
  font-family: 'Circular-Bold';
`;

const UrgentIcon = styled.i`
  display: 'inline-block';
  width: '100%';
  margin-bottom: '16px';
`;

const TemplateInput = ({
  values,
  errors,
  touched,
  setFieldValue,
  noticeTemplates,
  canOnlySendByNoticeTemplate,
}) => {
  const getNoticeTemplates = () => {
    let templates = [];
    if (!canOnlySendByNoticeTemplate) {
      templates = [
        {
          label: getTranslatedString('landlordPortal.noticeForm.none'),
          value: 'none',
        },
      ];
    }
    if (noticeTemplates && noticeTemplates.length > 0) {
      let sortedTemplates = noticeTemplates.sort((a, b) => sortAlphaNum(a['label'], b['label']));
      templates = [...templates, ...sortedTemplates];
    }
    return templates;
  };

  const getSelectedTemplate = () => {
    return getTemplate(noticeTemplates, values.notice_template_id);
  };

  const getSelectedTemplateValue = (key, fallback) => {
    const template = getSelectedTemplate();
    return getTemplateValue(template, key, fallback);
  };

  const handleChange = (templateId) => {
    setFieldValue('notice_template_id', templateId);
    setFieldValue('mode', modes.SELECTING_PROPERTY);
    setFieldValue('properties', []);
    const template = getTemplate(noticeTemplates, templateId);
    if (template) applyTemplateFields(template);
  };

  const applyTemplateFields = (template) => {
    setFieldValue('effective_dates', []);
    setFieldValue('properties', []);
    setFieldValue('post_date', undefined);
    setFieldValue('expiration_date', undefined);
    setFieldValue('image', undefined);
    setFieldValue('new_attachments', undefined);
    setFieldValue('attachments', undefined);
    setFieldValue('notice_template_id', undefined);

    if (template) {
      ['name', 'description', 'image'].forEach((key) => {
        if (template[`notice_${key}`]) {
          setFieldValue(key, template[`notice_${key}`]);
        }
      });

      const attachments = template.attachments.map((attachment, index) => {
        return { ...attachment, template: true };
      });

      setFieldValue('attachments', attachments);
      setFieldValue('notice_template_id', template.id);
      setFieldValue('notice_type', template.notice_type);
    }
  };

  const isUrgentTemplate = () => {
    const selectedTemplate = getSelectedTemplate();
    return selectedTemplate && selectedTemplate.urgent;
  };

  const fieldLabel = useMemo(() => {
    return `
      ${getTranslatedString('landlordPortal.noticeForm.selectTemplate')}
      ${
        !canOnlySendByNoticeTemplate
          ? getTranslatedString('landlordPortal.noticeForm.optional')
          : ''
      }`;
  }, [canOnlySendByNoticeTemplate]);

  const renderWarning = () => {
    const deliveryType = getSelectedTemplateValue('delivery', 'any');
    if (deliveryType === 'any' && !isUrgentTemplate()) return null;

    let warningString = '';

    if (deliveryType === 'single_property') {
      warningString = getTranslatedString('landlordPortal.noticeForm.singlePropertyWarning');
    } else if (deliveryType === 'single_unit') {
      warningString = getTranslatedString('landlordPortal.noticeForm.singleUnitWarning');
    }

    return (
      <>
        <Spacer mb={2} />
        <Row gutter={16} style={{ textAlign: 'center' }}>
          <UrgentText>
            {(featureEnabled('notice_restrictions') || isUrgentTemplate()) && (
              <UrgentIcon className="em em-warning" />
            )}
            <br />
            {isUrgentTemplate() && (
              <TenantResidentVerbiageHelper
                translatedTenant={getTranslatedString(
                  'landlordPortal.noticeForm.thisTemplateHasBeenMarkedTenant'
                )}
                translatedResident={getTranslatedString(
                  'landlordPortal.noticeForm.thisTemplateHasBeenMarkedResident'
                )}
              />
            )}{' '}
            {featureEnabled('notice_restrictions') && warningString}
          </UrgentText>
        </Row>
      </>
    );
  };

  return (
    <>
      <Spacer mb={1}>
        <Row gutter={16}>
          <StyledColMb8 span={24}>
            <StyledTitle>{getTranslatedString('landlordPortal.noticeForm.templates')}</StyledTitle>
          </StyledColMb8>
          <Col span={24}>
            <Form.Item label={fieldLabel}>
              <FullWidthSelect
                name="notice_template_id"
                value={values.notice_template_id}
                onChange={handleChange}
                placeholder="Select template..."
                size="large"
                error={touched.notice_template_id && errors.notice_template_id}
              >
                {getNoticeTemplates().map((template) => (
                  <Select.Option value={template.value} key={template.value}>
                    {template.label}
                  </Select.Option>
                ))}
              </FullWidthSelect>
            </Form.Item>
            {renderWarning()}
          </Col>
        </Row>
      </Spacer>
    </>
  );
};

export default connect(
  (store) => ({
    noticeTemplates: store.landlordNotices.notice_templates,
    canOnlySendByNoticeTemplate: store.userTypes.canOnlySendByNoticeTemplate,
  }),
  {}
)(TemplateInput);
